import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  FormControlLabel,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import {
  getUnitDetails,
  updateLevelById,
  updateAudioFileForQuestion,
  archiveQuestionsForLevel,
  restoreQuestionsForLevel,
  reorderQuestionsForLevel,
  createQuestionsForLevel,
  archiveLevelById,
  restoreArchivedLevelById,
} from "../service/service";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import AudioPlayerComponent from "../components/AudioPlayer";
import ConfirmationModal from "../components/ConfirmationModal";
import FilterMenu from "../components/FilterMenu";
import Layout from "../components/Layout";
import ReorderModal from "../components/ReorderModal";
import { Add, ExpandLess, ExpandMore } from "@mui/icons-material";
import NewQuestionDialog from "../components/NewQuestionDialog";

const questionTypes = [
  "MC",
  "MCM",
  "ACIQ",
  "ACMC",
  "CT",
  "ACIQM",
  "UVI",
  "LAR",
  "CTA",
]; // Example options for question type

const hintTypes = ["audio", "text"];

// Create a custom alert component
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const UnitEditPage = () => {
  const { courseId, unitId } = useParams();
  const [unitData, setUnitData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [selectedQuestionsByLevel, setSelectedQuestionsByLevel] = useState({}); // Track selected question ids per level.
  const [archivingLevelId, setArchivingLevelId] = useState(null);
  const [restoringLevelId, setRestoringLevelId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const [file, setFile] = useState(null); // State to store the file

  // const [activeLevelId, setActiveLevelId] = useState(null);
  const [questionFilter, setQuestionFilter] = useState("active");
  const [isFetchingLevels, setIsFetchingLevels] = useState(true);
  const [collapsedLevels, setCollapsedLevels] = useState({});
  const [collapsedQuestions, setCollapsedQuestions] = useState({});
  const [levelLoading, setLevelLoading] = useState({});
  const [questionLoading, setQuestionLoading] = useState({});

  const filterLabels = {
    active: "Active filter",
    archived: "Archived filter",
    all: "All filter",
  };

  const filterLevelsForDisplay = (levelsToFilter, filter) => {
    const hasArchivedQuestions = (lvl) =>
      Array.isArray(lvl?.questions) && lvl.questions.some((q) => q.isQuestionArchived);

    switch (filter) {
      case "archived":
        // Show archived levels, plus any active levels that contain archived questions.
        return levelsToFilter.filter((lvl) => lvl.isArchived || hasArchivedQuestions(lvl));
      case "active":
        return levelsToFilter.filter((lvl) => !lvl.isArchived);
      case "all":
      default:
        return levelsToFilter;
    }
  };

  const sortLevelsForDisplay = (levelsToSort) => {
    return [...levelsToSort].sort((a, b) => {
      const archivedDiff = Number(Boolean(a?.isArchived)) - Number(Boolean(b?.isArchived));
      if (archivedDiff !== 0) return archivedDiff;

      const orderA = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER;
      const orderB = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;

      return (a?.name || "").localeCompare(b?.name || "");
    });
  };

  const [questionDialogState, setQuestionDialogState] = useState({
    open: false,
    level: null,
    startingOrder: 0,
  });

  const questionFilterMenuLabel =
    filterLabels[questionFilter] || filterLabels.active;

  const shouldDisplayQuestion = (filter, isQuestionArchived) => {
    switch (filter) {
      case "archived":
        return isQuestionArchived;
      case "all":
        return true;
      case "active":
      default:
        return !isQuestionArchived;
    }
  };

  const openBulkQuestionDialog = (level, visibleQuestionCount) => {
    if (!level) {
      return;
    }
    setQuestionDialogState({
      open: true,
      level,
      startingOrder: visibleQuestionCount,
    });
  };

  const closeBulkQuestionDialog = () => {
    setQuestionDialogState({
      open: false,
      level: null,
      startingOrder: 0,
    });
  };

  const handleBulkQuestionSave = async (questionsToCreate) => {
    console.log("Prepared question payloads:", { questionsToCreate });
    if (questionsToCreate.length) {
      const bulkResponse = await createQuestionsForLevel(questionsToCreate, {
        notificationType: "new-questions",
        notificationMessage:
          "fresh questions just dropped. Tap to jump in now.",
      });

      if (!bulkResponse?.success) {
        const errorMessage =
          bulkResponse?.error?.message ||
          bulkResponse?.message ||
          "Failed to save questions for the level.";
        throw new Error(errorMessage);
      }

      setSeverity("success");
      setMessage(
        `Level ${questionDialogState.level.order} • ${bulkResponse?.message}` ||
          "New questions saved successfully!",
      );
      setOpen(true);

      setLevels((prevLevels) =>
        prevLevels.map((level) =>
          level._id === questionDialogState.level._id
            ? {
                ...level,
                questions: [...level.questions, ...bulkResponse.data],
              }
            : level,
        ),
      );

      closeBulkQuestionDialog();
    }
  };

  const fileInputRef = useRef(null);

  const fetchUnitDetails = useCallback(
    async (showLoader = true) => {
      if (showLoader) setIsFetchingLevels(true);
      try {
        const { data } = await getUnitDetails(courseId, unitId);
        setUnitData(data.unit);
        setLevels(data.levels);
        setSelectedQuestionsByLevel({});
      } catch (error) {
        console.error("Failed to fetch unit details:", error);
      } finally {
        if (showLoader) setIsFetchingLevels(false);
      }
    },
    [courseId, unitId],
  );

  useEffect(() => {
    fetchUnitDetails();
  }, [fetchUnitDetails]);

  const handleFilterChange = (nextFilter) => {
    if (nextFilter === questionFilter) {
      return;
    }
    setQuestionFilter(nextFilter);
  };

  const handleLevelChange = (index, key, value) => {
    const updatedLevels = levels.map((level, i) =>
      i === index ? { ...level, [key]: value } : level,
    );
    setLevels(updatedLevels);
  };

  const handleQuestionChange = (levelIndex, questionIndex, key, value) => {
    const updatedLevels = [...levels];
    updatedLevels[levelIndex].questions[questionIndex][key] = value;
    updatedLevels[levelIndex].questions[questionIndex].isModified = true;
    // const updatedLevels = levels.map((level, i) =>
    //   i === levelIndex
    //     ? { ...level, questions: level.questions.map((q, j) => (j === questionIndex ? { ...q, [key]: value } : q)) }
    //     : level
    // );

    setLevels(updatedLevels);
  };

  const handleOptionChange = (
    levelIndex,
    questionIndex,
    optionIndex,
    value,
  ) => {
    const updatedLevels = [...levels];

    const { questionType, Answer } =
      updatedLevels[levelIndex].questions[questionIndex];
    const oldOptionValue =
      updatedLevels[levelIndex].questions[questionIndex].options[optionIndex];
    if (questionType === "MCM") {
      const answerArray = Answer.split("; ");
      if (answerArray.includes(oldOptionValue)) {
        const newAnswer = answerArray.filter(
          (option) => option !== oldOptionValue,
        );
        newAnswer.push(value);
        updatedLevels[levelIndex].questions[questionIndex].Answer =
          newAnswer.join("; ");
      }
    } else {
      if (oldOptionValue === Answer) {
        updatedLevels[levelIndex].questions[questionIndex].Answer = value;
      }
    }

    updatedLevels[levelIndex].questions[questionIndex].options[optionIndex] =
      value;
    updatedLevels[levelIndex].questions[questionIndex].isModified = true;
    setLevels(updatedLevels);
  };

  const handleAnswerChange = (
    levelIndex,
    questionIndex,
    questionType,
    Answer,
    isChecked = true,
  ) => {
    setLevels((prevLevels) => {
      const nextLevels = [...prevLevels];
      const question = nextLevels[levelIndex]?.questions?.[questionIndex];
      if (!question) return prevLevels;

      const singleAnswerTypes = ["MC", "ACIQ", "ACMC"];
      const multiAnswerTypes = ["MCM", "ACIQM"];

      // Allow the selection of multiple answers
      const toggleMultiAnswer = () => {
        const current = new Set(
          (question.Answer || "")
            .split("; ")
            .map((item) => item.trim())
            .filter(Boolean),
        );
        if (isChecked) current.add(Answer);
        else current.delete(Answer);
        question.Answer = Array.from(current).join("; ");
      };

      if (singleAnswerTypes.includes(questionType)) {
        question.Answer = Answer;
      } else if (multiAnswerTypes.includes(questionType)) {
        toggleMultiAnswer();
      } else {
        console.warn("Unknown questionType", questionType);
        return prevLevels;
      }

      question.isModified = true;
      return nextLevels;
    });
  };

  const toggleLevelCollapse = (levelId) => {
    setCollapsedLevels((prev) => {
      const isCollapsed = Boolean(prev[levelId]);

      if (isCollapsed) {
        // Expanding: show a brief spinner while rendering heavy content
        setLevelLoading((loadingPrev) => ({ ...loadingPrev, [levelId]: true }));
        setTimeout(() => {
          setLevelLoading((loadingPrev) => ({
            ...loadingPrev,
            [levelId]: false,
          }));
        }, 150);
      }

      return { ...prev, [levelId]: !prev[levelId] };
    });
  };

  const toggleQuestionCollapse = (levelId, questionId) => {
    setCollapsedQuestions((prev) => {
      const levelMap = prev[levelId] || {};
      const isCollapsed = Boolean(levelMap[questionId]);

      if (isCollapsed) {
        setQuestionLoading((loadingPrev) => ({
          ...loadingPrev,
          [levelId]: { ...(loadingPrev[levelId] || {}), [questionId]: true },
        }));
        setTimeout(() => {
          setQuestionLoading((loadingPrev) => ({
            ...loadingPrev,
            [levelId]: { ...(loadingPrev[levelId] || {}), [questionId]: false },
          }));
        }, 150);
      }

      const nextLevelMap = { ...levelMap, [questionId]: !levelMap[questionId] };
      return { ...prev, [levelId]: nextLevelMap };
    });
  };

  // Toggle per-question selection for upcoming bulk archive.
  const toggleQuestionSelection = (levelId, questionId, isChecked) => {
    setSelectedQuestionsByLevel((prev) => {
      const current = new Set(prev[levelId] || []);
      if (isChecked) {
        current.add(questionId);
      } else {
        current.delete(questionId);
      }

      // Update the selected questions for this level
      const next = { ...prev };
      if (current.size) {
        next[levelId] = Array.from(current);
      } else {
        delete next[levelId];
      }

      return next;
    });
  };

  // Mark selected questions as archived in the backend, then clean them up locally.
  const handleArchiveSelectedQuestions = async (levelId) => {
    const targetLevel = levels.find((level) => level._id === levelId);
    if (!targetLevel) {
      return;
    }

    const idsForLevel = (selectedQuestionsByLevel[levelId] || []).filter(
      (questionId) => {
        const question = targetLevel.questions?.find(
          (item) => item._id === questionId,
        );
        return question && !question.isQuestionArchived;
      },
    );

    if (!idsForLevel.length) {
      return;
    }

    setArchivingLevelId(levelId);
    try {
      const response = await archiveQuestionsForLevel(levelId, idsForLevel);

      // Update local state to remove archived questions
      setLevels((prevLevels) =>
        prevLevels.map((level) => {
          if (level._id !== levelId) {
            return level;
          }
          const idsToMark = new Set(idsForLevel);

          const markedQuestions = level.questions.map((question) =>
            idsToMark.has(question._id)
              ? { ...question, isQuestionArchived: true, order: null }
              : { ...question },
          );

          const visibleQuestions = markedQuestions
            .filter((question) => !question.isQuestionArchived)
            .sort((a, b) => a.order - b.order);

          const orderLookup = new Map();
          visibleQuestions.forEach((question, index) => {
            orderLookup.set(question._id, index + 1);
          });

          const normalizedQuestions = markedQuestions.map((question) =>
            question.isQuestionArchived
              ? question
              : {
                  ...question,
                  order: orderLookup.get(question._id) || question.order,
                },
          );

          return {
            ...level,
            questions: normalizedQuestions,
          };
        }),
      );

      // Clear selected questions for this level after deletion
      setSelectedQuestionsByLevel((prev) => {
        const next = { ...prev };
        const remainingIds = (next[levelId] || []).filter(
          (id) => !idsForLevel.includes(id),
        );
        if (remainingIds.length) {
          next[levelId] = remainingIds;
        } else {
          delete next[levelId];
        }
        return next;
      });

      setSeverity("success");
      setMessage(response?.message || "Selected questions removed.");

      return response;
    } catch (error) {
      console.error("Failed to archive questions for level:", error);
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to archive selected questions.";
      setSeverity("error");
      setMessage(errorMessage);
      throw error;
    } finally {
      setOpen(true);
      setArchivingLevelId(null);
    }
  };

  const handleRestoreSelectedQuestions = async (levelId) => {
    const levelIsArchived = levels.some((lvl) => lvl._id === levelId && lvl.isArchived);
    if (levelIsArchived) {
      setSeverity("error");
      setMessage("Cannot restore questions while the level is archived. Restore the level instead.");
      setOpen(true);
      return;
    }

    const targetLevel = levels.find((level) => level._id === levelId);
    if (!targetLevel) {
      return;
    }

    const idsForLevel = (selectedQuestionsByLevel[levelId] || []).filter(
      (questionId) => {
        const question = targetLevel.questions?.find(
          (item) => item._id === questionId,
        );
        return question && question.isQuestionArchived;
      },
    );

    if (!idsForLevel.length) {
      return;
    }

    setRestoringLevelId(levelId);
    try {
      const response = await restoreQuestionsForLevel(levelId, idsForLevel);
      const restoredMeta = response?.data?.updatedQuestions || [];
      const restoredLookup = new Map(
        restoredMeta.map((item) => [item._id, item]),
      );

      setLevels((prevLevels) =>
        prevLevels.map((level) => {
          if (level._id !== levelId) {
            return level;
          }

          const updatedQuestions = level.questions.map((question) => {
            const restored = restoredLookup.get(question._id);
            if (!restored) {
              return question;
            }

            return {
              ...question,
              isQuestionArchived: false,
              order: restored.order,
            };
          });

          return {
            ...level,
            questions: updatedQuestions,
          };
        }),
      );

      setSelectedQuestionsByLevel((prev) => {
        const next = { ...prev };
        const remainingIds = (next[levelId] || []).filter(
          (id) => !idsForLevel.includes(id),
        );
        if (remainingIds.length) {
          next[levelId] = remainingIds;
        } else {
          delete next[levelId];
        }
        return next;
      });

      setSeverity("success");
      setMessage(response?.message || "Selected questions restored.");

      return response;
    } catch (error) {
      console.error("Failed to restore questions for level:", error);
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to restore selected questions.";
      setSeverity("error");
      setMessage(errorMessage);
      throw error;
    } finally {
      setOpen(true);
      setRestoringLevelId(null);
    }
  };

  const handleReorderQuestionConfirm =
    (levelId) => async (newQuestionOrder) => {
      const result = await reorderQuestionsForLevel(newQuestionOrder);

      console.log("Reorder Result", result);

      if (!result?.success) {
        setSeverity("error");
        setMessage(
          result?.message || "Failed to reorder questions. Please try again.",
        );
        setOpen(true);
        return;
      }

      setLevels((prevLevels) => {
        return prevLevels.map((level) => {
          if (level._id !== levelId) {
            return level;
          }

          const updatedQuestions = level.questions
            .map((question) => {
              const updatedQuestion = result?.data?.updatedQuestions.find(
                (q) => q._id === question._id,
              );

              return updatedQuestion
                ? { ...question, ...updatedQuestion }
                : question;
            })
            .sort((a, b) => {
              const orderA =
                typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
              const orderB =
                typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
              return orderA - orderB;
            });

          return {
            ...level,
            questions: updatedQuestions,
          };
        });
      });

      setSeverity("success");
      setMessage("Questions reordered successfully");
      setOpen(true);
    };

  const handleUpdateLevel = async (levelId, levelData, levelIndex) => {
    setLoading(true);
    try {
      const modifiedQuestions = levelData.questions.filter(
        (questionData) => questionData.isModified,
      );
      // levelData.questions = modifiedQuestions;
      console.log(">> Level Data: ", levelData);
      const res = await updateLevelById(levelId, levelData);
      console.log("Updated Res --- ---", res);
      if (res.isValid) {
        setMessage(res.message); // Set the success message
        setOpen(true); // Open the toast
      } else {
        setMessage("Error occurred while updating levels and question");
        setSeverity("error"); // Red toast for error
      }
    } catch (error) {
      console.error("Failed to update level:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveLevel = async (levelId) => {
    setLevelLoading((prev) => ({ ...prev, [levelId]: true }));
    try {
      const res = await archiveLevelById(levelId);
      if (res?.success) {
        setSeverity("success");
        setMessage(res?.message || "Level archived successfully");
        setOpen(true);
        await fetchUnitDetails(false);
      } else {
        setSeverity("error");
        setMessage(res?.message || "Failed to archive level");
        setOpen(true);
      }
    } catch (error) {
      console.error("Failed to archive level:", error);
      setSeverity("error");
      setMessage("Failed to archive level. Please try again.");
      setOpen(true);
      throw error;
    } finally {
      setLevelLoading((prev) => ({ ...prev, [levelId]: false }));
    }
  };

  const handleRestoreArchivedLevel = async (levelId) => {
    const targetLevel = levels.find((lvl) => lvl._id === levelId);
    if (!targetLevel) return;

    const idsForLevel = (selectedQuestionsByLevel[levelId] || []).filter((questionId) => {
      const question = targetLevel.questions?.find((item) => item._id === questionId);
      return question && question.isQuestionArchived;
    });

    if (!idsForLevel.length) {
      setSeverity("error");
      setMessage("Select at least one archived question to restore the level.");
      setOpen(true);
      return;
    }

    const parentUnitId = targetLevel.parentUnitId || unitId;

    setLevelLoading((prev) => ({ ...prev, [levelId]: true }));
    try {
      const restoreQuestionsResp = await restoreQuestionsForLevel(levelId, idsForLevel);

      if (restoreQuestionsResp?.success === false) {
        setSeverity("error");
        setMessage(restoreQuestionsResp?.message || "Failed to restore selected questions.");
        setOpen(true);
        return;
      }

      const levelResp = await restoreArchivedLevelById(levelId, { parentUnitId });

      if (levelResp?.success === false || levelResp?.isValid === false) {
        setSeverity("error");
        setMessage(levelResp?.message || "Failed to restore level.");
        setOpen(true);
        return;
      }

      await fetchUnitDetails(false);

      setSeverity("success");
      setMessage(levelResp?.message || "Level and selected questions restored successfully.");
      setOpen(true);
    } catch (error) {
      console.error("Failed to restore archived level:", error);
      setSeverity("error");
      setMessage("Failed to restore level. Please try again.");
      setOpen(true);
    } finally {
      setLevelLoading((prev) => ({ ...prev, [levelId]: false }));
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false); // Close the toast when done
  };

  const isOptionCorrect = (option, answer, type) => {
    let result = false;

    switch (type) {
      case "MC": {
        result = option?.toLowerCase() === answer.toLowerCase();
        break;
      }

      case "ACIQ": {
        result = option?.toLowerCase() === answer.toLowerCase();
        break;
      }
      case "ACIQM": {
        result = answer?.split("; ").includes(option);
        break;
      }

      case "MCM": {
        result = answer?.split("; ").includes(option);
        break;
      }
      default:
        console.log("Unknown type", type);
    }

    return result;
  };

  // Handle file selection
  const handleFileSelectOfReplaceAudio = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "audio/mpeg") {
      setFile(selectedFile);
    } else {
      alert("Please upload a valid MP3 file.");
    }
  };

  // Handle form submission
  const replaceAudio = async (e, question, type) => {
    e.preventDefault();

    const fieldToUpdate = type;
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    // Prepare the form data
    const formData = new FormData();
    formData.append("file", file); // Add the file to the form data
    formData.append("questionId", question._id); // Replace with actual data
    formData.append("courseId", question.parentCourseId); // Replace with actual data
    formData.append("unitId", question.parentUnitId); // Replace with actual data
    formData.append("levelId", question.parentLevelId); // Replace with actual data
    formData.append("fieldToUpdate", fieldToUpdate); // Replace with actual data

    const res = await updateAudioFileForQuestion(formData);
    console.log("Res of replace audio", res);

    if (res.message) {
      alert("File Uploaded Successfully");
    } else {
      alert("Something went wrong!!");
    }

    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset the file input field
    }

    // try {
    //   // Post the file to your backend
    //   const response = await axios.post("http://localhost:5000/api/upload", formData, {
    //     headers: {
    //       "Content-Type": "multipart/form-data", // Required for file uploads
    //     },
    //   });

    //   setUploadStatus("File uploaded successfully!");
    //   console.log("Response:", response.data);
    // } catch (error) {
    //   setUploadStatus("File upload failed.");
    //   console.error("Error uploading file:", error);
    // }
  };

  if (isFetchingLevels)
    return (
      <Backdrop open={isFetchingLevels} sx={{ zIndex: 1 }}>
        <CircularProgress />
      </Backdrop>
    );

  const filteredLevelsForView = sortLevelsForDisplay(
    filterLevelsForDisplay(levels, questionFilter),
  );

  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          height: "100%",
          width: "100%",
          backgroundColor: "background.default",
          p: 4,
        }}
      >
        <Paper elevation={0} sx={{ padding: "2rem", width: "100%" }}>
          {/* Unit Information (Read-only) */}
          <Typography variant="h5" gutterBottom>
            Edit Unit: {unitData.name}
          </Typography>
          <Typography variant="subtitle1">Order: {unitData.order}</Typography>
          <Typography variant="subtitle1">
            Bonus: {unitData.isBonus ? "Yes" : "No"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 1.5,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              endIcon={<Add />}
              component={Link}
              to={`/course/${courseId}/units/${unitId}/add-level`}
            >
              Add Level
            </Button>

            <FilterMenu
              label={questionFilterMenuLabel}
              selectedValue={questionFilter}
              onChange={handleFilterChange}
            />
          </Box>

          {/* Level Sections */}
          {/* Display this text if there are no archived entries to show */}
          {questionFilter === "archived" && filteredLevelsForView.length === 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" color="text.secondary" align="center">
                No archived entries found. Try archiving a level or question.
              </Typography>
            </Box>
          )}

          {/* Display this text if there are no active entries to show */}
          {questionFilter === "active" && filteredLevelsForView.length === 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" color="text.secondary" align="center">
                No active entries found. Add or restore a level or question.
              </Typography>
            </Box>
          )}

          {filteredLevelsForView.map((level, levelIndex) => {
            const selectedQuestionsForLevel =
              selectedQuestionsByLevel[level._id] || [];

            const visibleQuestionCount =
              level.questions?.filter((q) => !q.isQuestionArchived).length || 0;

            const selectedActiveQuestions =
              level.questions?.filter(
                (q) =>
                  selectedQuestionsForLevel.includes(q._id) &&
                  !q.isQuestionArchived,
              ) || [];

            const selectedArchivedQuestions =
              level.questions?.filter(
                (q) =>
                  selectedQuestionsForLevel.includes(q._id) &&
                  q.isQuestionArchived,
              ) || [];

            const orderedQuestions = [...(level.questions || [])].sort(
              (a, b) => {
                const archivedDiff =
                  Number(Boolean(a?.isQuestionArchived)) -
                  Number(Boolean(b?.isQuestionArchived));
                if (archivedDiff !== 0) return archivedDiff;

                const aOrder = Number.isFinite(a?.order)
                  ? a.order
                  : Number.MAX_SAFE_INTEGER;
                const bOrder = Number.isFinite(b?.order)
                  ? b.order
                  : Number.MAX_SAFE_INTEGER;
                if (aOrder !== bOrder) return aOrder - bOrder;

                return (a?.question || "").localeCompare(b?.question || "");
              },
            );

            const hasActiveSelections = selectedActiveQuestions.length > 0;
            const hasArchivedSelections = selectedArchivedQuestions.length > 0;
            const isLevelCollapsed = Boolean(collapsedLevels[level._id]);
            const isLevelLoading = Boolean(levelLoading[level._id]);

            return (
              <Paper
                key={level._id}
                sx={{
                  mt: 3,
                  mb: 3,
                  padding: "24px",
                  border: "1px solid silver",
                }}
              >
                <Grid container sx={{ padding: "24px" }}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <Typography sx={{ mt: 2, mb: 2 }} variant="h6">
                        Level {level.order == null ? "-" : level.order}
                      </Typography>
                    </Grid>
                    {level.isArchived && (
                      <Grid item>
                        <Typography
                          component="span"
                          variant="subtitle2"
                          color="error.main"
                        >
                          Archived
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {level.name || ""}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        endIcon={
                          isLevelCollapsed ? <ExpandMore /> : <ExpandLess />
                        }
                        onClick={() => toggleLevelCollapse(level._id)}
                      >
                        {isLevelCollapsed ? "Expand Level" : "Collapse Level"}
                      </Button>
                    </Grid>
                    {questionFilter === "active" && (
                      <Grid item>
                        <ConfirmationModal
                          title="Archive Level"
                          description={`Archiving Level ${level.order ?? ""} will hide it (and its questions) from learners. You can still view it under the archived filter.`}
                          helperText="This action also archives all questions under this level."
                          confirmLabel="Archive"
                          cancelLabel="Cancel"
                          loading={Boolean(levelLoading[level._id])}
                          disabled={Boolean(levelLoading[level._id])}
                          renderTrigger={({ openModal, disabled, loading }) => (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={openModal}
                              disabled={disabled}
                            >
                              {loading ? "Archiving..." : "Archive Level"}
                            </Button>
                          )}
                          onConfirm={() => handleArchiveLevel(level._id)}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {!isLevelCollapsed && !isLevelLoading && (
                    <>
                      <Grid item xs={12} md={12}>
                        <TextField
                          label="Level Name"
                          variant="outlined"
                          fullWidth
                          value={level.name}
                          onChange={(e) =>
                            handleLevelChange(
                              levelIndex,
                              "name",
                              e.target.value,
                            )
                          }
                        />
                      </Grid>

                      <Grid container columnGap={2} sx={{ mt: 2 }}>
                        <ReorderModal
                          title={`Reorder Questions for Level ${level.order}`}
                          description={`Reorder the questions for Level ${level.order} using drag and drop.`}
                          questions={level.questions.filter(
                            (q) => !q.isQuestionArchived,
                          )}
                          confirmLabel="Confirm"
                          cancelLabel="Cancel"
                          onConfirm={handleReorderQuestionConfirm(level._id)}
                          isDisabled={
                            questionFilter !== "active" ||
                            visibleQuestionCount < 2
                          }
                        />

                        {questionFilter === "active" && (
                          <>
                            <Button
                              variant="contained"
                              color="primary"
                              sx={{ placeSelf: "center" }}
                              onClick={() =>
                                openBulkQuestionDialog(
                                  level,
                                  visibleQuestionCount,
                                )
                              }
                              endIcon={<Add />}
                            >
                              Add Question(s)
                            </Button>
                          </>
                        )}

                        <Grid item xs={12} md={12}>
                          <Divider
                            sx={{
                              borderBottomWidth: 3, // Set the thickness
                              borderColor: "primary.main",
                              mt: 4,
                              mb: 4, // Set the color to blue
                            }}
                          />
                        </Grid>
                      </Grid>

                      {/* Questions */}
                      {orderedQuestions.map((question) => {
                        const isQuestionArchived = Boolean(
                          question.isQuestionArchived,
                        );
                        if (
                          !shouldDisplayQuestion(
                            questionFilter,
                            isQuestionArchived,
                          )
                        ) {
                          return null;
                        }

                        const questionIndex = level.questions.findIndex(
                          (item) => item._id === question._id,
                        );
                        if (questionIndex === -1) return null;

                        const isSelected = selectedQuestionsForLevel.includes(
                          question._id,
                        );
                        const checkboxLabel = isQuestionArchived
                          ? "Select to restore question"
                          : "Select to archive question";

                        const questionOrderLabel = Number.isFinite(
                          question.order,
                        )
                          ? question.order
                          : "-";

                        const isQuestionCollapsed = Boolean(
                          collapsedQuestions[level._id]?.[question._id],
                        );
                        const questionPreview = question.question || "Question";
                        const isQuestionLoading = Boolean(
                          questionLoading[level._id]?.[question._id],
                        );

                        return (
                          <Grid
                            key={question._id}
                            sx={{ width: "100%", mb: 2 }}
                          >
                            <Grid
                              container
                              alignItems="center"
                              spacing={1}
                              sx={{ mt: 2, mb: 1 }}
                            >
                              <Grid item>
                                <Typography variant="h6">
                                  Question: {questionOrderLabel}
                                  {isQuestionArchived && (
                                    <Typography
                                      component="span"
                                      variant="subtitle2"
                                      color="error.main"
                                      sx={{ ml: 1 }}
                                    >
                                      Archived
                                    </Typography>
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item xs>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  title={questionPreview}
                                >
                                  {questionPreview}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Button
                                  variant="outlined"
                                  color="inherit"
                                  size="small"
                                  endIcon={
                                    isQuestionCollapsed ? (
                                      <ExpandMore />
                                    ) : (
                                      <ExpandLess />
                                    )
                                  }
                                  onClick={() =>
                                    toggleQuestionCollapse(
                                      level._id,
                                      question._id,
                                    )
                                  }
                                >
                                  {isQuestionCollapsed
                                    ? "Expand Question"
                                    : "Collapse Question"}
                                </Button>
                              </Grid>
                              {!(questionFilter === "all") && (
                                <Grid item>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={isSelected}
                                        disabled={
                                          archivingLevelId === level._id ||
                                          restoringLevelId === level._id
                                        }
                                        onChange={(event) =>
                                          toggleQuestionSelection(
                                            level._id,
                                            question._id,
                                            event.target.checked,
                                          )
                                        }
                                      />
                                    }
                                    label={checkboxLabel}
                                  />
                                </Grid>
                              )}
                            </Grid>

                            {!isQuestionCollapsed && !isQuestionLoading && (
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <FormControl fullWidth>
                                    <InputLabel>Question Type</InputLabel>
                                    <Select
                                      value={question.questionType || ""}
                                      label="Question Type"
                                      onChange={(e) =>
                                        handleQuestionChange(
                                          levelIndex,
                                          questionIndex,
                                          "questionType",
                                          e.target.value,
                                        )
                                      }
                                    >
                                      <MenuItem value="">None</MenuItem>
                                      {questionTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {type}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} md={8}>
                                  <TextField
                                    label="Question"
                                    variant="outlined"
                                    fullWidth
                                    value={question.question}
                                    onChange={(e) =>
                                      handleQuestionChange(
                                        levelIndex,
                                        questionIndex,
                                        "question",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>

                                {["ACIQ", "CTA", "CT", "ACIQM"].includes(
                                  question.questionType,
                                ) && (
                                  <div className="audio-container">
                                    <AudioPlayerComponent
                                      audioUrl={question.url}
                                    />
                                    <form
                                      style={{ marginLeft: "20px" }}
                                      onSubmit={(e) =>
                                        replaceAudio(e, question, "url")
                                      }
                                    >
                                      <label>Replace Audio </label>
                                      <input
                                        type="file"
                                        accept=".mp3"
                                        onChange={(e) =>
                                          handleFileSelectOfReplaceAudio(e)
                                        }
                                        ref={fileInputRef}
                                      />
                                      <button
                                        type="submit"
                                        style={{ marginLeft: "10px" }}
                                      >
                                        Upload
                                      </button>
                                    </form>
                                  </div>
                                )}

                                {!["ACMC", "UVI", "CT", "LAR"].includes(
                                  question.questionType,
                                ) && (
                                  <Grid item xs={12}>
                                    <Table
                                      sx={{
                                        background: "primary.lighterShade",
                                      }}
                                    >
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Option</TableCell>
                                          <TableCell>
                                            Is Correct Answer
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {Array.isArray(question.options) &&
                                          question.options.map(
                                            (option, optionIndex) => (
                                              <TableRow key={option._id}>
                                                <TableCell>
                                                  <TextField
                                                    value={option}
                                                    fullWidth
                                                    onChange={(e) =>
                                                      handleOptionChange(
                                                        levelIndex,
                                                        questionIndex,
                                                        optionIndex,
                                                        e.target.value,
                                                      )
                                                    }
                                                  />
                                                </TableCell>
                                                <TableCell>
                                                  <Checkbox
                                                    checked={isOptionCorrect(
                                                      option,
                                                      question.Answer,
                                                      question.questionType,
                                                    )}
                                                    onChange={(e) =>
                                                      handleAnswerChange(
                                                        levelIndex,
                                                        questionIndex,
                                                        question.questionType,
                                                        option,
                                                        e.target.checked,
                                                      )
                                                    }
                                                  />
                                                </TableCell>
                                              </TableRow>
                                            ),
                                          )}
                                      </TableBody>
                                    </Table>
                                  </Grid>
                                )}

                                {question &&
                                ["ACMC"].includes(question.questionType) ? (
                                  <Grid item>
                                    <Table
                                      sx={{
                                        background: "primary.lighterShade",
                                      }}
                                    >
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Option</TableCell>
                                          <TableCell>Audio Clip</TableCell>
                                          <TableCell>
                                            Is Correct Answer
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {Object.entries(
                                          question.options || {},
                                        ).map(
                                          (
                                            [optionName, audioUrl],
                                            optionIndex,
                                          ) => (
                                            <TableRow key={optionName}>
                                              <TableCell>
                                                {optionName}
                                              </TableCell>
                                              <TableCell>
                                                <div className="audio-container">
                                                  <AudioPlayerComponent
                                                    audioUrl={audioUrl}
                                                  />
                                                  <form
                                                    style={{
                                                      marginLeft: "20px",
                                                    }}
                                                    onSubmit={(e) =>
                                                      replaceAudio(
                                                        e,
                                                        question,
                                                        `options.${optionName}`,
                                                      )
                                                    }
                                                  >
                                                    <label>
                                                      Replace Audio{" "}
                                                    </label>
                                                    <input
                                                      type="file"
                                                      accept=".mp3"
                                                      onChange={(e) =>
                                                        handleFileSelectOfReplaceAudio(
                                                          e,
                                                        )
                                                      }
                                                      ref={fileInputRef}
                                                    />
                                                    <button
                                                      type="submit"
                                                      style={{
                                                        marginLeft: "10px",
                                                      }}
                                                    >
                                                      Upload
                                                    </button>
                                                  </form>
                                                </div>
                                              </TableCell>
                                              <TableCell>
                                                <Checkbox
                                                  checked={
                                                    question.Answer ===
                                                    optionName
                                                  }
                                                  onChange={(e) => {
                                                    handleAnswerChange(
                                                      levelIndex,
                                                      questionIndex,
                                                      question.questionType,
                                                      optionName,
                                                      e.target.checked,
                                                    );
                                                  }}
                                                />
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  </Grid>
                                ) : null}
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="Note Brief"
                                    variant="outlined"
                                    fullWidth
                                    value={question.noteBrief}
                                    onChange={(e) =>
                                      handleQuestionChange(
                                        levelIndex,
                                        questionIndex,
                                        "noteBrief",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label="Note Full"
                                    variant="outlined"
                                    fullWidth
                                    value={question.noteFull}
                                    onChange={(e) =>
                                      handleQuestionChange(
                                        levelIndex,
                                        questionIndex,
                                        "noteFull",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                  <FormControl fullWidth>
                                    <InputLabel>Hint Type</InputLabel>
                                    <Select
                                      value={question.hintType || ""}
                                      label="Hint Type"
                                      onChange={(e) =>
                                        handleQuestionChange(
                                          levelIndex,
                                          questionIndex,
                                          "hintType",
                                          e.target.value,
                                        )
                                      }
                                    >
                                      <MenuItem value="">None</MenuItem>
                                      {hintTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                          {type}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                {(question.questionType === "UVI" ||
                                  question.questionType === "LAR") && (
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="Phrase"
                                      variant="outlined"
                                      fullWidth
                                      value={question.phrase}
                                      onChange={(e) =>
                                        handleQuestionChange(
                                          levelIndex,
                                          questionIndex,
                                          "phrase",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </Grid>
                                )}

                                {question.hintType === "text" && (
                                  <Grid item xs={12} md={8}>
                                    <TextField
                                      label="Hint"
                                      variant="outlined"
                                      fullWidth
                                      value={question.hint}
                                      onChange={(e) =>
                                        handleQuestionChange(
                                          levelIndex,
                                          questionIndex,
                                          "hint",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </Grid>
                                )}

                                {question.hintType === "audio" ? (
                                  <div>
                                    <Grid item xs={12} md={8}>
                                      <InputLabel
                                        sx={{ marginLeft: 2, marginTop: 1 }}
                                      >
                                        Hint Audio
                                      </InputLabel>
                                    </Grid>
                                    <div className="audio-container">
                                      <AudioPlayerComponent
                                        audioUrl={question.hintUrl}
                                      />
                                      <form
                                        style={{ marginLeft: "20px" }}
                                        onSubmit={(e) =>
                                          replaceAudio(e, question, `hintUrl`)
                                        }
                                      >
                                        <label>Replace Audio </label>
                                        <input
                                          type="file"
                                          accept=".mp3"
                                          onChange={(e) =>
                                            handleFileSelectOfReplaceAudio(e)
                                          }
                                          ref={fileInputRef}
                                        />
                                        <button
                                          type="submit"
                                          style={{ marginLeft: "10px" }}
                                        >
                                          Upload
                                        </button>
                                      </form>
                                    </div>
                                  </div>
                                ) : (
                                  ""
                                )}

                                <Grid item xs={12} md={12}>
                                  <Divider
                                    sx={{
                                      borderBottomWidth: 3, // Set the thickness
                                      borderColor: "primary.main",
                                      mt: 4,
                                      mb: 4, // Set the color to blue
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            )}
                            {isQuestionLoading && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  py: 2,
                                }}
                              >
                                <CircularProgress size={20} />
                              </Box>
                            )}
                          </Grid>
                        );
                      })}
                    </>
                  )}
                  {isLevelLoading && (
                    <Box
                      sx={{
                        width: "100%",
                        py: 3,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                </Grid>

                {/* Save Button for Each Level */}
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  {questionFilter !== "all" && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        handleUpdateLevel(
                          level._id,
                          levels[levelIndex],
                          levelIndex,
                        )
                      }
                      disabled={loading}
                    >
                      {loading ? "Saving..." : `Save Level ${level.order}`}
                    </Button>
                  )}

                  {questionFilter === "active" && (
                    <ConfirmationModal
                      buttonLabel="Archive Selected"
                      loadingLabel="Archiving..."
                      confirmLabel="Confirm"
                      cancelLabel="Cancel"
                      title="Confirm Archive"
                      helperText="Archiving hides these questions from learners. You can restore them later if needed."
                      levelOrder={level.order}
                      selectedQuestions={selectedActiveQuestions}
                      disabled={
                        !hasActiveSelections || archivingLevelId === level._id
                      }
                      loading={archivingLevelId === level._id}
                      onConfirm={() =>
                        handleArchiveSelectedQuestions(level._id)
                      }
                    />
                  )}

                  {questionFilter === "archived" && (
                    <ConfirmationModal
                      buttonLabel="Restore Selected"
                      loadingLabel="Restoring..."
                      confirmLabel="Restore"
                      cancelLabel="Cancel"
                      title="Restore Questions"
                      description={`Level ${level.order} archived questions will be restored and appended to the end of the list.`}
                      levelOrder={level.order}
                      selectedQuestions={selectedArchivedQuestions}
                      disabled={
                        !hasArchivedSelections ||
                        restoringLevelId === level._id ||
                        level.isArchived
                      }
                      loading={restoringLevelId === level._id}
                      onConfirm={() =>
                        handleRestoreSelectedQuestions(level._id)
                      }
                    />
                  )}

                  {level.isArchived && questionFilter === "archived" && (
                    <ConfirmationModal
                      buttonLabel="Restore Level"
                      loadingLabel="Restoring..."
                      confirmLabel="Restore Level"
                      cancelLabel="Cancel"
                      title="Restore Level"
                      description={`Restore Level ${level.order} and the selected archived questions.`}
                      helperText="Select archived questions above, then restore the level."
                      selectedQuestions={selectedArchivedQuestions}
                      disabled={
                        !hasArchivedSelections || levelLoading[level._id]
                      }
                      loading={Boolean(levelLoading[level._id])}
                      onConfirm={() => handleRestoreArchivedLevel(level._id)}
                    />
                  )}
                </Box>
              </Paper>
            );
          })}
        </Paper>

        {/* Single bulk-question dialog instance to avoid multiple stacked dialogs */}
        <NewQuestionDialog
          open={questionDialogState.open}
          onClose={closeBulkQuestionDialog}
          onSave={handleBulkQuestionSave}
          courseId={courseId}
          unitId={unitId}
          level={questionDialogState.level}
          startingOrder={questionDialogState.startingOrder}
        />

        <Snackbar
          open={open}
          autoHideDuration={4000} // Toast will auto close after 4 seconds
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }} // Position of the toast
        >
          <Alert onClose={handleClose} severity={severity}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default UnitEditPage;
